import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import Row from '@inspire/ui/chakra/layout/Row';
import Image from 'next/image';
import { lightBeigeBg } from '@inspire/ui/static';
import Footer from 'components/layout/Footer';
import {
  emphasisButtonProps,
  roundButtonProps,
} from '@inspire/ui/chakra/theme';
import TestimonialsSlider from './TestimonialsSlider';
import QA from './QA';
import Timeline from './Timeline';
import QuoteOpenSrc from '../../../public/img/landing-pages/campagne/quote-green-1.png';
import QuoteCloseSrc from '../../../public/img/landing-pages/campagne/quote-green-2.png';
import { cdnURL } from '@inspire/util/cdn';
import Link from 'components/general/Link';
import LogoSrc from '@inspire/ui/assets/logo.svg';
import { accessSSO } from 'lib/sso';
import { CONNEXION_PATH, LOGGED_IN_HOME_PATH } from 'lib/paths';
import {
  GTM_ACTION_LANDING_PAGE_LYCEEN,
  GTM_CATEGORY_LANDING_PAGES,
  pushEvent,
} from 'lib/gtm';

const A1ConnectLandingPageLyceensMetiersCampagne = ({ pageConfig }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Box>
      {/* Row Image et Description */}
      <Row
        minHeight="80vh"
        bg="transparent url('/img/landing-pages/campagne/welcome-lp-campagne.jpg') no-repeat 58% 0%"
        bgSize="cover"
        position="relative"
        display="flex"
        flexDirection="column"
        justifyContent="space-around"
      >
        {/* Superposition semi-transparente sur l'image de bienvenue en desktop */}
        <Box
          width="100%"
          height="100%"
          backgroundColor="rgba(0, 0, 0, 0.8)"
          zIndex={0}
          top="0"
          left="0"
          bottom="0"
          right="0"
          position="absolute"
        />

        {/* Logo INSPIRE */}
        <Box ml="10" mt={['0', '0', '10']}>
          <Image
            alt="Logo INSPIRE"
            src={LogoSrc}
            height={isMobile ? '150%' : ''}
            width={isMobile ? '150%' : ''}
          />
        </Box>

        {/* Texte description */}
        <Box
          justifyContent="center"
          display="flex"
          flexDirection="column"
          zIndex={1}
        >
          {/* Texte */}
          <Heading as="h2" size="2xl" color={'white'} textAlign={'center'}>
            {pageConfig.description.header}
          </Heading>
          <Text color={'white'} fontSize={'xl'} textAlign={'center'}>
            {pageConfig.description.text}
          </Text>
        </Box>

        {/* Bouton inscription */}
        <Box
          display="flex"
          flexDir="column"
          alignItems={'center'}
          justifyContent={'center'}
        >
          <Button
            {...roundButtonProps}
            {...emphasisButtonProps}
            p={8}
            {...roundButtonProps}
            onClick={() => {
              pushEvent(
                GTM_CATEGORY_LANDING_PAGES,
                GTM_ACTION_LANDING_PAGE_LYCEEN,
                "Trouver mes pistes d'études"
              );
              accessSSO({
                targetUrl: `${CONNEXION_PATH}?redirectUrl=${LOGGED_IN_HOME_PATH}`,
              });
            }}
          >
            <Text
              fontSize={['lg', 'lg', 'xl']}
              fontWeight="bold"
              letterSpacing={2}
            >
              Trouver mes pistes d'études
            </Text>
          </Button>

          <Text
            color={'white'}
            fontSize={['lg', 'lg', 'xl']}
            textAlign={'center'}
            mb="10"
            position={'relative'}
          >
            Pssst : c’est GRATUIT !
          </Text>
        </Box>
      </Row>

      <Row bgColor={lightBeigeBg} py={12}>
        <Container maxW="container.lg">
          <Timeline
            timelineContent={pageConfig.timelineContent}
            timelineSubCTA={pageConfig.timelineContent.buttonSubtitle}
            profil={pageConfig.profil}
            ctaLabel={pageConfig.ctaLabel}
          />
        </Container>
      </Row>

      {/* Header text */}
      <Heading textAlign="left" fontWeight={'normal'} pl={['10', '10', '40']}>
        Ils en parlent, <b>et en bien</b>.
      </Heading>
      {/* Box avis */}
      <Box
        mx="auto"
        width={['90%', '90%', '50%']}
        display="flex"
        flexDir="column"
        bgColor="white"
        p={['10', '10', '100']}
      >
        {/* Quote ouvrante */}
        <Box justifyContent="left" display="flex" flexDir="row">
          <Image src={QuoteOpenSrc} alt="quote" />
        </Box>

        {/* Slider */}
        <TestimonialsSlider
          testimonials={pageConfig.testimonials}
          showQuotes={false}
        />

        {/* Quote fermante */}
        <Box justifyContent="right" display="flex" flexDir="row" mt="20px">
          <Image src={QuoteCloseSrc} alt="quote" />
        </Box>
      </Box>

      {/* Row vert chiffres */}
      <Row
        bg="#20D9A0"
        display="flex"
        flexDir="column"
        textAlign="center"
        alignItems="center"
      >
        {/* Row text et image */}
        <Box
          display="flex"
          flexDir="row"
          justifyContent={['center', 'center', 'space-around']}
          width={[null, null, '100%']}
          pl={[null, null, '100px']}
          pr={[null, null, '100px']}
        >
          {/* Col textes */}
          <Box display="flex" flexDir="column" width="90%">
            {/* Header text */}
            <Heading
              color="white"
              textShadow="2px 5px 1px rgba(0, 0, 0, 0.2)"
              textAlign={['center', 'center', 'left']}
            >
              <b>INSPIRE en quelques chiffres</b>
            </Heading>

            {/* Row chiffres */}
            <Box
              display="flex"
              flexDir={['column', 'column', 'row']}
              justifyContent={['center', null, 'space-between']}
            >
              {/* row élaireur inscrits */}
              <Box
                display="flex"
                flexDir={['column', 'row']}
                textAlign={['center', 'center', 'left']}
              >
                <Heading
                  color="white"
                  textShadow="2px 5px 1px rgba(0, 0, 0, 0.2)"
                  width="300px"
                  textAlign={'center'}
                >
                  13 000
                </Heading>
                <Text
                  color="white"
                  fontSize={['lg', 'lg', 'xl']}
                  textShadow="2px 5px 1px rgba(0, 0, 0, 0.1)"
                  textAlign={'left'}
                  mt={['0', '10']}
                  flex="1"
                >
                  Étudiants Éclaireurs de différents cursus (licences, BUT, BTS,
                  prépa,...) prêts à répondre à tes questions
                </Text>
              </Box>
            </Box>

            <Box
              display="flex"
              flexDir={['column', 'column', 'row']}
              justifyContent={['center', null, 'space-between']}
            >
              {/* Col pourcentage */}
              <Box
                display="flex"
                flexDir={['column', 'row']}
                textAlign={['center', 'center', 'left']}
              >
                <Heading
                  color="white"
                  textShadow="2px 5px 1px rgba(0, 0, 0, 0.2)"
                  width="300px"
                  textAlign={'center'}
                >
                  {pageConfig.chiffres.pourcentage}
                </Heading>
                <Text
                  color="white"
                  fontSize={['lg', 'lg', 'xl']}
                  textShadow="2px 5px 1px rgba(0, 0, 0, 0.1)"
                  textAlign={'left'}
                  mt={['0', '10']}
                  flex="1"
                >
                  {pageConfig.chiffres.text}
                </Text>
              </Box>
            </Box>

            <Box
              display="flex"
              flexDir={['column', 'column', 'row']}
              justifyContent={['center', null, 'space-between']}
            >
              {/* Col pourcentage */}
              <Box
                display="flex"
                flexDir={['column', 'row']}
                textAlign={['center', 'center', 'left']}
              >
                <Box flex="1"></Box>
                <Text
                  color="white"
                  fontSize={['lg', 'lg', 'xl']}
                  textShadow="2px 5px 1px rgba(0, 0, 0, 0.1)"
                  textAlign={'left'}
                  mt="10"
                  flex="10"
                  pr="10"
                >
                  <b>Bon à savoir :</b>
                  <br />
                  Le programme INSPIRE est labellisé par le ministère de
                  l’Enseignement supérieur et agréé par le ministère de
                  l’Education nationale.
                </Text>

                <Box width="220px" bg="white" p="10px">
                  <Box
                    pos="relative"
                    width="200px"
                    minW="100px"
                    minH="170px"
                    bg="white"
                    border="5px solid white"
                    overflow="hidden"
                  >
                    <Link
                      href="https://www.enseignementsup-recherche.gouv.fr/fr"
                      isExternal
                    >
                      <Image
                        src={cdnURL('/1850-mesri_2020_header_1343552.png')}
                        alt=""
                        layout="fill"
                        objectFit="contain"
                      />
                    </Link>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Image en desktop */}
          {!isMobile && (
            <Box width="20%" pt={50}>
              <Image src={pageConfig.chiffres.imageSrc} alt="" />
            </Box>
          )}
        </Box>

        <Button
          {...roundButtonProps}
          onClick={() => {
            pushEvent(
              GTM_CATEGORY_LANDING_PAGES,
              GTM_ACTION_LANDING_PAGE_LYCEEN,
              'Je m’inscris'
            );
            accessSSO({
              targetUrl: `${CONNEXION_PATH}?redirectUrl=${LOGGED_IN_HOME_PATH}`,
            });
          }}
          m="10"
        >
          Je m’inscris
        </Button>
      </Row>

      <Row pt={8} pb={12}>
        <QA QAContent={pageConfig.QAContent} />
      </Row>

      <Footer isLight={true} />
    </Box>
  );
};

export default A1ConnectLandingPageLyceensMetiersCampagne;
